import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

// Firma and NIP are order attributes, not an invoice-issuing integration.
// Keep one in-memory draft for the drawer and cart page. Section HTML can have
// been rendered before the latest keystroke, so it must not replace this draft.
/** @typedef {{ Firma: string, NIP: string }} InvoiceValues */
/** @type {InvoiceValues | null} */
let values = null;
/** @type {InvoiceValues | null} */
let confirmed = null;
let edited = false;
let saveFailed = false;
/** @type {Promise<void> | null} */
let pendingSave = null;
let waitingForCheckout = false;
let checkoutStarted = false;

/** @param {ParentNode} root @returns {InvoiceValues} */
const readFields = (root) => ({
  Firma: /** @type {HTMLInputElement | null} */ (root.querySelector('[name="attributes[Firma]"]'))?.value ?? '',
  NIP: /** @type {HTMLInputElement | null} */ (root.querySelector('[name="attributes[NIP]"]'))?.value ?? '',
});
/** @param {InvoiceValues} value @returns {InvoiceValues} */
const normalise = (value) => ({ Firma: value.Firma.trim(), NIP: value.NIP.replace(/[\s-]/g, '') });
/** @param {InvoiceValues | null} a @param {InvoiceValues | null} b */
const equal = (a, b) => a?.Firma === b?.Firma && a?.NIP === b?.NIP;

/** @param {string} nip */
const nipPoprawny = (nip) => {
  if (!/^\d{10}$/.test(nip)) return false;
  const wagi = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const suma = wagi.reduce((acc, waga, i) => acc + waga * Number(nip[i]), 0);
  return suma % 11 === Number(nip[9]);
};

function syncFields() {
  document.querySelectorAll('etolie-cart-invoice').forEach((element) => {
    if (element instanceof EtolieCartInvoice) element.preserveFields(element);
  });
}

function saveValues() {
  if (pendingSave) return pendingSave;
  pendingSave = (async () => {
    try {
      // Never abort a write: Shopify may already be processing it. Wait for
      // each response before sending newer values, including explicit clearing.
      while (!checkoutStarted && values && !equal(normalise(values), confirmed)) {
        const submitted = normalise(values);
        const response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', { body: JSON.stringify({ attributes: submitted }) })
        );
        const data = await response.json();
        if (!response.ok || data.errors || !data.attributes) throw new Error('Invoice attributes were not saved');
        const saved = { Firma: String(data.attributes.Firma ?? ''), NIP: String(data.attributes.NIP ?? '') };
        if (!equal(saved, submitted)) throw new Error('Invoice attributes did not match the request');
        confirmed = submitted;
        saveFailed = false;
      }
    } catch (error) {
      saveFailed = true;
    } finally {
      syncFields();
    }
  })().finally(() => { pendingSave = null; });
  return pendingSave;
}

const queueSave = debounce(saveValues, 300);

class EtolieCartInvoice extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (values === null) {
      values = readFields(this);
      confirmed = normalise(values);
    }
    this.preserveFields(this);
    if (edited && (values.Firma || values.NIP)) {
      const accordion = this.querySelector('accordion-custom');
      accordion?.setAttribute('open-by-default-on-mobile', '');
      accordion?.setAttribute('open-by-default-on-desktop', '');
      const details = this.querySelector('details');
      if (details) details.open = true;
    }
  }

  // Called by morph before it copies a stale cart section into this component.
  /** @param {Element} root */
  preserveFields(root) {
    if (values === null) return;
    for (const key of ['Firma', 'NIP']) {
      const input = /** @type {HTMLInputElement | null} */ (root.querySelector(`[name="attributes[${key}]"]`));
      if (!input) continue;
      if (input.value !== values[key]) input.value = values[key];
      if (input.getAttribute('value') !== values[key]) input.setAttribute('value', values[key]);
    }
    const nip = normalise(values).NIP;
    const hint = /** @type {HTMLElement | null} */ (root.querySelector('[data-cart-invoice-hint]'));
    if (hint) hint.hidden = nip === '' || nipPoprawny(nip);
    const error = /** @type {HTMLElement | null} */ (root.querySelector('[data-cart-invoice-error]'));
    if (error) error.hidden = !saveFailed;
    if (saveFailed) {
      const details = root.querySelector('details');
      if (details) details.open = true;
    }
  }

  save = () => {
    // Capture before the debounce window: a quantity response may arrive while
    // the shopper is still typing. Keep raw text in the UI to preserve the caret.
    values = readFields(this);
    edited = true;
    saveFailed = false;
    syncFields();
    queueSave();
  };

  retry = () => {
    queueSave.cancel();
    return saveValues();
  };
}

if (!customElements.get('etolie-cart-invoice')) {
  customElements.define('etolie-cart-invoice', EtolieCartInvoice);

  // Native checkout remains the final write. It must not race a previous AJAX
  // write or serialize duplicate/stale drawer fields on the full cart page.
  document.addEventListener('formdata', (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('.cart-form') || !edited || !values) return;
    for (const [key, value] of Object.entries(normalise(values))) event.formData.set(`attributes[${key}]`, value);
  });

  document.addEventListener('submit', async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('.cart-form') || !edited || checkoutStarted) return;
    queueSave.cancel();
    syncFields();
    if (!pendingSave) return;
    event.preventDefault();
    if (waitingForCheckout) return;
    waitingForCheckout = true;
    const form = event.target;
    const submitter = event.submitter;
    // Stop waiting after 5 seconds, but never race a native POST against an
    // unresolved older write. Show the existing retry message instead.
    let timeout;
    const settled = await Promise.race([
      pendingSave.then(() => true),
      new Promise((resolve) => { timeout = setTimeout(() => resolve(false), 5000); }),
    ]);
    clearTimeout(timeout);
    queueSave.cancel();
    waitingForCheckout = false;
    if (!settled) {
      saveFailed = true;
      syncFields();
      return;
    }
    // A cart quantity response can have replaced the form while we waited.
    const currentForm = form.isConnected ? form : document.querySelector('form.cart-form');
    if (!(currentForm instanceof HTMLFormElement)) return;
    const currentSubmitter = submitter?.isConnected ? submitter : document.querySelector('button[name="checkout"]');
    checkoutStarted = true;
    currentForm.requestSubmit(currentSubmitter instanceof HTMLButtonElement ? currentSubmitter : undefined);
  }, true);

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.resource?.item_count !== 0) return;
    // Only a confirmed empty cart clears the invoice request. A failed removal
    // must leave the company details intact.
    values = { Firma: '', NIP: '' };
    edited = true;
    saveFailed = false;
    syncFields();
    queueSave();
  });
  window.addEventListener('pageshow', () => { checkoutStarted = false; });
}
